/*
  Vibes TCG — scroll & attention behavior.
  Vanilla port of the Next.js site's React components:
    SmoothScroll.tsx, Hero.tsx, ShopGrid.tsx, BoosterCinema.tsx,
    PlaymatSection.tsx, TabAttention.tsx.
  Assumes globals `gsap`, `ScrollTrigger`, `Lenis` are loaded first
  (vibes-gsap.js, vibes-scrolltrigger.js, vibes-lenis.js — all deferred,
  in that order). Every lookup is null-checked so pages without a given
  section never throw.
*/
(function () {
  "use strict";

  /* ------------------------------------------------------------------ *
   * SmoothScroll.tsx — Lenis + GSAP ticker wiring
   * ------------------------------------------------------------------ */
  function initSmoothScroll() {
    if (typeof Lenis === "undefined") return;
    var reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduced) return;

    var lenis = new Lenis({
      lerp: 0.12,
      wheelMultiplier: 1,
    });

    lenis.on("scroll", ScrollTrigger.update);

    var raf = function (time) {
      lenis.raf(time * 1000);
    };
    gsap.ticker.add(raf);
    gsap.ticker.lagSmoothing(0);
  }

  /* ------------------------------------------------------------------ *
   * Hero.tsx — entrance zoom, scroll-away parallax, cursor tilt
   * ------------------------------------------------------------------ */
  function initHero(mm) {
    var root = document.getElementById("top");
    if (!root) return;

    mm.add("(prefers-reduced-motion: no-preference)", function () {
      // opening moment: key art settles from a gentle zoom
      var bg = root.querySelector("[data-hero-bg]");
      if (bg) {
        gsap.fromTo(bg, { scale: 1.09 }, { scale: 1, duration: 1.6, ease: "power2.out" });
      }

      // characters fly off in different directions as you scroll away
      gsap.utils.toArray(root.querySelectorAll("[data-speed]")).forEach(function (el) {
        gsap.to(el, {
          y: Number(el.dataset.speed),
          x: Number(el.dataset.drift || 0),
          rotation: Number(el.dataset.spin || 0),
          ease: "none",
          scrollTrigger: {
            trigger: root,
            start: "top top",
            end: "bottom top",
            scrub: true,
          },
        });
      });

      // hero product gently scales/rises away
      var product = root.querySelector("[data-hero-product]");
      if (product) {
        gsap.to(product, {
          y: -40,
          scale: 1.06,
          ease: "none",
          scrollTrigger: {
            trigger: root,
            start: "top top",
            end: "bottom top",
            scrub: true,
          },
        });
      }

      // 3D tilt that follows the cursor over the box
      var tilt = root.querySelector("[data-tilt]");
      if (tilt) {
        gsap.set(tilt, { transformPerspective: 900 });
        var rotX = gsap.quickTo(tilt, "rotationX", { duration: 0.5, ease: "power2.out" });
        var rotY = gsap.quickTo(tilt, "rotationY", { duration: 0.5, ease: "power2.out" });
        var scaleTo = gsap.quickTo(tilt, "scale", { duration: 0.4, ease: "power2.out" });
        var onMove = function (e) {
          var r = tilt.getBoundingClientRect();
          var nx = (e.clientX - r.left) / r.width - 0.5;
          var ny = (e.clientY - r.top) / r.height - 0.5;
          rotY(nx * 18);
          rotX(-ny * 14);
        };
        var onEnter = function () {
          scaleTo(1.04);
        };
        var onLeave = function () {
          rotX(0);
          rotY(0);
          scaleTo(1);
        };
        tilt.addEventListener("mousemove", onMove);
        tilt.addEventListener("mouseenter", onEnter);
        tilt.addEventListener("mouseleave", onLeave);
        return function () {
          tilt.removeEventListener("mousemove", onMove);
          tilt.removeEventListener("mouseenter", onEnter);
          tilt.removeEventListener("mouseleave", onLeave);
        };
      }
    });
  }

  /* ------------------------------------------------------------------ *
   * ShopGrid.tsx — transform-only reveal drift for product grids
   * ------------------------------------------------------------------ */
  function initShopGrid(mm) {
    var grids = gsap.utils.toArray("[data-grid]");
    if (!grids.length) return;

    mm.add("(prefers-reduced-motion: no-preference)", function () {
      // transform-only drift: cards are never hidden (conversion rule —
      // products must be readable the instant they're in viewport)
      grids.forEach(function (grid) {
        if (!grid.children.length) return;
        gsap.from(grid.children, {
          y: 46,
          duration: 0.7,
          ease: "power3.out",
          stagger: 0.06,
          immediateRender: false,
          scrollTrigger: {
            trigger: grid,
            start: "top 92%",
            once: true,
          },
        });
      });
    });
  }

  /* ------------------------------------------------------------------ *
   * BoosterCinema.tsx — pinned scene: kinetic type, pack burst, beats
   * ------------------------------------------------------------------ */
  function initBoosterCinema(mm) {
    var root = document.getElementById("flagship");
    if (!root) return;

    mm.add("(prefers-reduced-motion: no-preference) and (min-width: 768px)", function () {
      var panels = gsap.utils.toArray(root.querySelectorAll("[data-beat]"));
      if (!panels.length) return;
      var seg = Number(root.dataset.sceneSpeed) || 45;
      var finalHold = 400;
      var pinEnd = "+=" + (panels.length * seg + finalHold) + "%";
      var scene = function () {
        return {
          trigger: root,
          start: "top top",
          end: pinEnd,
          scrub: true,
        };
      };

      var stage = root.querySelector("[data-stage]");
      if (stage) {
        ScrollTrigger.create({
          trigger: root,
          start: "top top",
          end: pinEnd,
          pin: stage,
          scrub: true,
        });
      }

      // kinetic backdrop type slides opposite directions
      gsap.utils.toArray(root.querySelectorAll("[data-kinetic]")).forEach(function (el) {
        var dir = Number(el.dataset.kinetic);
        gsap.fromTo(
          el,
          { xPercent: dir > 0 ? 0 : -22 },
          { xPercent: dir > 0 ? -22 : 0, ease: "none", scrollTrigger: scene() }
        );
      });

      // booster packs burst out of the box — kept on the box's side so
      // they never cross into the copy
      var packTop = root.querySelector("[data-pack-top]");
      var packBottom = root.querySelector("[data-pack-bottom]");
      if (packTop || packBottom) {
        gsap.set([packTop, packBottom].filter(Boolean), { xPercent: -50, yPercent: -50 });
      }
      if (packTop) {
        gsap.fromTo(
          packTop,
          { x: 0, y: 40, rotate: 4, scale: 0.55, autoAlpha: 0 },
          { x: -70, y: -300, rotate: 16, scale: 1, autoAlpha: 1, ease: "power1.inOut", scrollTrigger: scene() }
        );
      }
      if (packBottom) {
        gsap.fromTo(
          packBottom,
          { x: -10, y: 20, rotate: -6, scale: 0.55, autoAlpha: 0 },
          { x: -250, y: 140, rotate: -22, scale: 1, autoAlpha: 1, ease: "power1.inOut", scrollTrigger: scene() }
        );
      }

      // both corner penguins drift gently upward as the scene plays
      var cross = root.querySelector("[data-cross]");
      if (cross) {
        gsap.fromTo(cross, { y: 130, rotate: 8 }, { y: -50, rotate: -5, ease: "none", scrollTrigger: scene() });
      }
      var riser = root.querySelector("[data-riser]");
      if (riser) {
        gsap.fromTo(riser, { y: 140, rotate: -10 }, { y: -60, rotate: 8, ease: "none", scrollTrigger: scene() });
      }

      // product rotates & scales through the whole scene
      var box = root.querySelector("[data-box]");
      if (box) {
        gsap.fromTo(
          box,
          { rotate: -7, scale: 0.92 },
          {
            rotate: 5,
            scale: 1.08,
            ease: "none",
            scrollTrigger: {
              trigger: root,
              start: "top top",
              end: pinEnd,
              scrub: true,
            },
          }
        );
      }

      // copy beats crossfade as the scroll progresses
      panels.forEach(function (panel, i) {
        gsap.fromTo(
          panel,
          { autoAlpha: i === 0 ? 1 : 0, y: i === 0 ? 0 : 30 },
          {
            autoAlpha: 1,
            y: 0,
            ease: "power2.out",
            scrollTrigger: {
              trigger: root,
              start: "top+=" + (i * seg - (i === panels.length - 1 ? 26 : 14)) + "% top",
              end: "top+=" + (i * seg - (i === panels.length - 1 ? 12 : 0)) + "% top",
              scrub: true,
            },
          }
        );
        if (i < panels.length - 1) {
          gsap.to(panel, {
            autoAlpha: 0,
            y: -30,
            ease: "power2.in",
            scrollTrigger: {
              trigger: root,
              start: "top+=" + ((i + 1) * seg - 26) + "% top",
              end: "top+=" + ((i + 1) * seg - 12) + "% top",
              scrub: true,
            },
          });
        }
      });
    });
}
function initMobileBoosterCinema(mm) {
  var root = document.getElementById("flagship-mobile");
  if (!root) return;

  mm.add(
    "(prefers-reduced-motion: no-preference) and (max-width: 767px)",
    function () {
      var stage = root.querySelector("[data-mobile-cinema-stage]");
      var scenes = gsap.utils.toArray(
        root.querySelectorAll("[data-mobile-cinema-scene]")
      );

      var box = root.querySelector("[data-mobile-cinema-box]");
      var packA = root.querySelector("[data-mobile-cinema-pack-a]");
      var packB = root.querySelector("[data-mobile-cinema-pack-b]");

      if (!stage || !scenes.length) return;

      // Initial scene states
      gsap.set(scenes, {
        autoAlpha: 0,
        y: 18
      });

      gsap.set(scenes[0], {
        autoAlpha: 1,
        y: 0
      });

      /*
       * One master timeline controls EVERYTHING.
       * This is much more reliable while the section is pinned.
       */
      var tl = gsap.timeline({
        scrollTrigger: {
          trigger: root,
          start: "top top",
          end: "+=320%",
          pin: root,
          pinSpacing: true,
          scrub: 0.6,
          anticipatePin: 1
        }
      });

      /*
       * Scene 1 holds first.
       */
      tl.to({}, { duration: 1 });

      /*
       * Scene 1 -> Scene 2
       */
      if (scenes.length > 1) {
        tl.to(
          scenes[0],
          {
            autoAlpha: 0,
            y: -18,
            duration: 0.35,
            ease: "power2.in"
          }
        );

        tl.fromTo(
          scenes[1],
          {
            autoAlpha: 0,
            y: 18
          },
          {
            autoAlpha: 1,
            y: 0,
            duration: 0.35,
            ease: "power2.out"
          },
          "<0.12"
        );

        tl.to({}, { duration: 1 });
      }

      /*
       * Scene 2 -> Scene 3
       */
      if (scenes.length > 2) {
        tl.to(
          scenes[1],
          {
            autoAlpha: 0,
            y: -18,
            duration: 0.35,
            ease: "power2.in"
          }
        );

        tl.fromTo(
          scenes[2],
          {
            autoAlpha: 0,
            y: 18
          },
          {
            autoAlpha: 1,
            y: 0,
            duration: 0.35,
            ease: "power2.out"
          },
          "<0.12"
        );

        /*
         * Nice long final hold.
         */
        tl.to({}, { duration: 1.8 });
      }

      /*
       * Product motion throughout the entire sequence.
       */
      if (box) {
        tl.fromTo(
          box,
          {
            y: 8,
            rotation: -2,
            scale: 0.98
          },
          {
            y: -10,
            rotation: 2,
            scale: 1.03,
            ease: "none",
            duration: tl.duration()
          },
          0
        );
      }

      if (packA) {
        tl.fromTo(
          packA,
          {
            x: -8,
            y: 12,
            rotation: -10
          },
          {
            x: 10,
            y: -10,
            rotation: 4,
            ease: "none",
            duration: tl.duration()
          },
          0
        );
      }

      if (packB) {
        tl.fromTo(
          packB,
          {
            x: 8,
            y: 16,
            rotation: 10
          },
          {
            x: -10,
            y: -8,
            rotation: -4,
            ease: "none",
            duration: tl.duration()
          },
          0
        );
      }

      requestAnimationFrame(function () {
        ScrollTrigger.refresh();
      });
    }
  );
}

  /* ------------------------------------------------------------------ *
   * PlaymatSection.tsx — the mat drifts up slower than the scroll
   * ------------------------------------------------------------------ */
  function initPlaymat(mm) {
    var roots = document.querySelectorAll("[data-vibes-playmat]");
    if (!roots.length) return;

    mm.add("(prefers-reduced-motion: no-preference)", function () {
      roots.forEach(function (root) {
        var mat = root.querySelector("[data-mat]");
        if (!mat) return;
        // the mat drifts up slower than the scroll — depth without hiding content
        gsap.fromTo(
          mat,
          { yPercent: 14, scale: 1.12 },
          {
            yPercent: -8,
            scale: 1,
            ease: "none",
            scrollTrigger: {
              trigger: root,
              start: "top bottom",
              end: "bottom top",
              scrub: true,
            },
          }
        );
      });
    });
  }

  /* ------------------------------------------------------------------ *
   * TabAttention.tsx — after 5 minutes hidden, flash title/favicon + ping
   * ------------------------------------------------------------------ */
  function initTabAttention() {
    var AWAY_DELAY_MS = 300000; // how long the tab is hidden before we ping
    var FLASH_INTERVAL_MS = 1200;
    var flashTitles = ["Come back", "to Vibes"];

    var audioCtx = null;
    var awayTimer = null;
    var flashTimer = null;
    var originalTitle = document.title;
    var iconLink = document.querySelector('link[rel="icon"]');
    var originalIcon = iconLink ? iconLink.href : null;

    // pink alert dot favicon, drawn once
    var alertIcon = (function () {
      var c = document.createElement("canvas");
      c.width = 64;
      c.height = 64;
      var ctx = c.getContext("2d");
      if (!ctx) return null;
      var grad = ctx.createLinearGradient(0, 0, 64, 64);
      grad.addColorStop(0, "#7c3aed");
      grad.addColorStop(1, "#ff2e9f");
      ctx.fillStyle = grad;
      ctx.beginPath();
      ctx.arc(32, 32, 28, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = "#fff";
      ctx.font = "bold 40px system-ui";
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillText("!", 32, 35);
      return c.toDataURL("image/png");
    })();

    // browsers only allow sound after the user has interacted once
    var unlockAudio = function () {
      if (!audioCtx) {
        try {
          audioCtx = new AudioContext();
        } catch (e) {}
      }
      if (audioCtx) {
        audioCtx.resume().catch(function () {});
      }
    };
    window.addEventListener("pointerdown", unlockAudio, { passive: true });
    window.addEventListener("keydown", unlockAudio, { passive: true });

    var ping = function () {
      if (!audioCtx || audioCtx.state !== "running") return;
      var now = audioCtx.currentTime;
      [880, 660].forEach(function (freq, i) {
        var osc = audioCtx.createOscillator();
        var gain = audioCtx.createGain();
        osc.type = "sine";
        osc.frequency.value = freq;
        gain.gain.setValueAtTime(0, now + i * 0.18);
        gain.gain.linearRampToValueAtTime(0.12, now + i * 0.18 + 0.03);
        gain.gain.exponentialRampToValueAtTime(0.001, now + i * 0.18 + 0.4);
        osc.connect(gain).connect(audioCtx.destination);
        osc.start(now + i * 0.18);
        osc.stop(now + i * 0.18 + 0.45);
      });
    };

    var startFlashing = function () {
      var i = 0;
      ping();
      flashTimer = setInterval(function () {
        document.title = flashTitles[i % flashTitles.length];
        if (iconLink && alertIcon) {
          iconLink.href = i % 2 === 0 ? alertIcon : originalIcon || alertIcon;
        }
        i++;
      }, FLASH_INTERVAL_MS);
    };

    var stopFlashing = function () {
      if (flashTimer) clearInterval(flashTimer);
      flashTimer = null;
      document.title = originalTitle;
      if (iconLink && originalIcon) iconLink.href = originalIcon;
    };

    var onVisibility = function () {
      if (document.hidden) {
        awayTimer = setTimeout(startFlashing, AWAY_DELAY_MS);
      } else {
        if (awayTimer) clearTimeout(awayTimer);
        awayTimer = null;
        stopFlashing();
      }
    };
    document.addEventListener("visibilitychange", onVisibility);
  }

  /* ------------------------------------------------------------------ */
  function init() {
    if (typeof gsap !== "undefined" && typeof ScrollTrigger !== "undefined") {
      gsap.registerPlugin(ScrollTrigger);

      initSmoothScroll();

      var mm = gsap.matchMedia();
      initHero(mm);
      initShopGrid(mm);
      initBoosterCinema(mm);
      initMobileBoosterCinema(mm);
      initPlaymat(mm);
    }

    initTabAttention();
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
  document.addEventListener("shopify:section:load", function (event) {
  if (typeof gsap === "undefined" || typeof ScrollTrigger === "undefined") return;

  var section = event.target;

  if (section && section.querySelector("#flagship")) {
    ScrollTrigger.getAll().forEach(function (trigger) {
      if (
        trigger.trigger &&
        (
          trigger.trigger.id === "flagship" ||
          trigger.trigger.closest?.("#flagship")
        )
      ) {
        trigger.kill();
      }
    });

    var mm = gsap.matchMedia();
    initBoosterCinema(mm);

    requestAnimationFrame(function () {
      ScrollTrigger.refresh();
    });
  }
});
})();
